import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  Box,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SettingsIcon from "@mui/icons-material/Settings";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { diasRestantes, colorEstado } from "./subscriptions/utils";
import EnviarWADialog from "./EnviarWADialog";

const GRUPO_CONFIG = {
  activa: { label: "Clientes con suscripción activa", color: "success", bgcolor: "tint.revenue", descripcion: "Estos clientes tienen su suscripción al día." },
  por_vencer: { label: "Clientes por renovar", color: "warning", bgcolor: "tint.warning", descripcion: "La suscripción de estos clientes está próxima a vencer. Contactarlos puede asegurar la renovación." },
  vencida: { label: "Clientes con suscripción vencida", color: "error", bgcolor: "tint.danger", descripcion: "Estos clientes ya no tienen suscripción activa. Cada uno representa ingreso que puedes recuperar." },
};

export default function SuscripcionesDetalleDialog({ grupo, ventas, store, onClose, onOpenConfig }) {
  const cfg = GRUPO_CONFIG[grupo] || {};
  const [waLead, setWaLead] = useState(null);

  return (
    <>
      <Dialog open={Boolean(grupo)} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: cfg.bgcolor }}>
          <AutorenewIcon color={cfg.color} />
          {cfg.label || "Suscripciones"}
          <Chip label={`${ventas.length}`} size="small" color={cfg.color} sx={{ fontWeight: 700 }} />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {cfg.descripcion && (
            <Typography variant="body2" color="text.secondary" mb={2} mt={1}>
              {cfg.descripcion}
            </Typography>
          )}

          <List dense disablePadding>
            {ventas.map(v => {
              const lead = v.leadId ? store.leads.find(l => l.id === v.leadId) : null;
              const dias = diasRestantes(v);
              const tieneTelefono = Boolean(lead?.telefono);
              return (
                <ListItem key={v.id} sx={{ px: 0, borderBottom: "1px solid", borderColor: "divider" }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: colorEstado(grupo) }}>
                      {v.nombreCliente?.charAt(0)?.toUpperCase() || "?"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>{v.nombreCliente}</Typography>
                        <Chip
                          label={dias !== null ? (dias >= 0 ? `${dias}d restantes` : `${Math.abs(dias)}d vencido`) : "—"}
                          size="small"
                          sx={{ fontSize: 10, height: 18, bgcolor: colorEstado(grupo), color: "common.white", fontWeight: 700 }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption">{v.productoNombre || "—"}</Typography>
                        <Typography variant="caption" fontWeight={700}>
                          {store.config.moneda} {v.total.toLocaleString()}
                        </Typography>
                        {v.endDate && (
                          <Typography variant="caption" color="text.secondary">
                            vence: {v.endDate}
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    {tieneTelefono ? (
                      <Tooltip title="Enviar mensaje por WhatsApp">
                        <IconButton
                          size="small"
                          onClick={() => setWaLead(lead)}
                          sx={{ color: "#25d366" }}
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Este cliente no tiene teléfono registrado en su lead">
                        <Typography variant="caption" color="text.disabled" sx={{ cursor: "default" }}>Sin tel.</Typography>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>

          {(store.config.plantillasWA || []).length === 0 && onOpenConfig && (
            <Stack direction="row" spacing={1} alignItems="center" mt={2} p={1.5} sx={{ bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Aún no tienes plantillas de WhatsApp. Crea una para enviar mensajes más rápido.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => { onClose(); onOpenConfig(); }}
                sx={{ whiteSpace: "nowrap" }}
              >
                Crear plantilla
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {(store.config.plantillasWA || []).length > 0 && onOpenConfig && (
            <Button size="small" startIcon={<SettingsIcon />} onClick={() => { onClose(); onOpenConfig(); }}>
              Gestionar plantillas
            </Button>
          )}
          <Button onClick={onClose} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>

      <EnviarWADialog
        open={Boolean(waLead)}
        onClose={() => setWaLead(null)}
        store={store}
        lead={waLead}
      />
    </>
  );
}
